// v5 reference specs, transcribed from the design's own stylesheets.
//
// Every value here is copied from a rule in the reference — font family,
// weight, size, line-height and letter-spacing — so components do not each
// re-guess them. Where a rule reads `font: 800 15px/1 var(--f-b)` in the
// reference, it appears here as weight 800, size 15, lineHeight 1, UI face.
//
// Colours stay in global.css as --tl-* custom properties; this file is the
// type scale and the shared shapes only.

export const DISPLAY = "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif";
export const UI = "'Manrope', ui-sans-serif, system-ui, sans-serif";

export const EASE = 'cubic-bezier(.2,.7,.2,1)';
export const SPRING = 'cubic-bezier(.34,1.56,.64,1)';

// ── display type ────────────────────────────────────────────────────────
// Headings set their colour explicitly. MUI's Typography falls back to
// variant="body1" when no variant is given, and this theme's body1 carries
// color: #4A605B — so a heading rendered as <Typography component="h2">
// came out in the muted body colour, a flat near-black against mint rather
// than the brand's deep green. Never rely on inheritance for these.
// .h1  — hero headline
export const h1 = {
  color: 'var(--tl-g9)',
  fontFamily: DISPLAY,
  // Capped at 72 rather than the reference's 88: two lines at 88px
  // pushed the hero past a 1080p viewport on its own.
  fontSize: 'clamp(38px, 4.6vw, 64px)',
  fontWeight: 700,
  lineHeight: 0.95,
  letterSpacing: '-.035em',
  textWrap: 'balance',
  m: 0,
};

// .h2  — every section heading
export const h2 = {
  color: 'var(--tl-g9)',
  fontFamily: DISPLAY,
  fontSize: 'clamp(32px, 4.4vw, 56px)',
  fontWeight: 700,
  lineHeight: 1.02,
  letterSpacing: '-.035em',
  textWrap: 'balance',
  m: 0,
};

// .lead — the paragraph under a heading
export const lead = {
  fontFamily: UI,
  fontSize: 'clamp(16px, 1.3vw, 19px)',
  fontWeight: 400,
  lineHeight: 1.6,
  color: 'var(--tl-muted)',
  textWrap: 'pretty',
  m: 0,
};

// ── controls ────────────────────────────────────────────────────────────
// .eyebrow — mint pill
export const eyebrow = {
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  gap: '8px',
  padding: '7px 12px',
  borderRadius: 999,
  background: 'var(--tl-mint)',
  color: 'var(--tl-teal-d)',
  fontFamily: UI,
  fontSize: 12.5,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '.02em',
};

// .btn — dark pill with the sheen sweep on hover
export const btn = {
  position: 'relative',
  isolation: 'isolate',
  overflow: 'hidden',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  minHeight: 48,
  padding: '0 22px',
  border: 0,
  borderRadius: 999,
  background: 'var(--tl-g9)',
  color: '#fff',
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 800,
  lineHeight: 1,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: `transform .35s ${EASE}, box-shadow .35s ${EASE}, background-color .25s`,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,.35) 50%, transparent 70%)',
    transform: 'translateX(-130%)',
    transition: `transform .8s ${EASE}`,
  },
  '&:hover': { color: '#fff', transform: 'translateY(-2px)', boxShadow: 'var(--tl-sh-2)' },
  '&:hover::after': { transform: 'translateX(130%)' },
  '&:active': { transform: 'scale(.97)', transitionDuration: '.12s' },
};

export const btnYellow = {
  ...btn,
  background: 'var(--tl-yellow)',
  color: 'var(--tl-g9)',
  '&:hover': {
    color: 'var(--tl-g9)',
    transform: 'translateY(-2px)',
    boxShadow: '0 14px 30px -12px rgba(232,181,0,.7)',
  },
  '&:disabled': { opacity: 0.55, pointerEvents: 'none' },
};

// .pill — white section action
export const pill = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  minHeight: 44,
  padding: '0 18px',
  borderRadius: 999,
  background: '#fff',
  color: 'var(--tl-g9)',
  fontFamily: UI,
  fontSize: 14.5,
  fontWeight: 800,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'var(--tl-sh-1)',
  transition: `transform .3s ${EASE}, box-shadow .3s`,
  '&:hover': { transform: 'translateY(-2px)', boxShadow: 'var(--tl-sh-2)', background: '#fff', color: 'var(--tl-g9)' },
};

// .lnk — inline teal action
export const lnk = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  minHeight: 44,
  color: 'var(--tl-teal-d)',
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 800,
  textTransform: 'none',
  '&:hover': { color: 'var(--tl-g9)', background: 'transparent' },
};

// ── layout ──────────────────────────────────────────────────────────────
// .wrap
// The reference ships two stylesheets: the home page loads v5.css (1320px
// container, 24px gutter), every inner page loads tl5.css (1240px, 40px).
// `wrap` is the inner-page container — Packages, Tests and Test Detail. The
// home components carry the wider 1320 figure inline and never use this.
export const wrap = {
  width: '100%',
  maxWidth: 1240,
  marginInline: 'auto',
  paddingInline: 'clamp(16px, 4vw, 40px)',
};

// .sec
// Vertical rhythm between sections — one value, used everywhere, so the
// whole page can be tightened or loosened from here. The reference used
// clamp(56px, 7vw, 104px); across fifteen sections that was about 1,500px
// of blank space on a desktop.
export const SECTION_GAP = 'clamp(32px, 3.6vw, 56px)';

export const sec = { paddingBlock: SECTION_GAP };

// .sh — section header row; heading and action share a baseline
export const sh = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: '16px 24px',
  marginBottom: '28px',
};

// .tag
export const tag = {
  alignSelf: 'flex-start',
  padding: '5px 10px',
  borderRadius: 999,
  fontFamily: UI,
  fontSize: 12,
  fontWeight: 800,
  lineHeight: 1,
};

export const tagPackage = { ...tag, background: 'var(--tl-yellow)', color: 'var(--tl-g9)' };
export const tagTest = { ...tag, background: 'var(--tl-mint)', color: 'var(--tl-teal-d)' };

// .note
export const note = {
  marginTop: '14px',
  fontFamily: UI,
  fontSize: 13,
  color: 'var(--tl-muted-2)',
};

// ── inner-page shell ────────────────────────────────────────────────────
// .phero — the mint banner every inner page opens with, rounded off at the
// bottom, with a dot field fading in from the top right.
export const phero = {
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--tl-mint)',
  borderRadius: '0 0 36px 36px',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: 'radial-gradient(rgba(21,122,105,.18) 1.2px, transparent 1.2px)',
    backgroundSize: '22px 22px',
    WebkitMaskImage: 'radial-gradient(ellipse 70% 90% at 85% 0%, #000 10%, transparent 70%)',
    maskImage: 'radial-gradient(ellipse 70% 90% at 85% 0%, #000 10%, transparent 70%)',
    opacity: 0.9,
    pointerEvents: 'none',
  },
};

// .phero-in
export const pheroIn = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  // Reference had clamp(48,7vw,96) over clamp(40,5vw,72). Combined with
  // sec-s below it that left ~145px of empty page between the search bar
  // and the first result.
  paddingBlock: 'clamp(28px, 3.4vw, 52px) clamp(20px, 2.4vw, 34px)',
};

// .h-xl — inner-page headline
export const hXl = {
  color: 'var(--tl-g9)',
  fontFamily: DISPLAY,
  fontSize: 'clamp(38px, 5.4vw, 68px)',
  fontWeight: 700,
  letterSpacing: '-.04em',
  // 1.08 and balanced wrapping come from the reference's h1–h5 reset, which
  // is what gives its headlines their even line lengths.
  lineHeight: 1.08,
  textWrap: 'balance',
  m: 0,
};

// .h-s
export const hS = { color: 'var(--tl-g9)', fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', m: 0 };

// .sec-s
export const secS = { paddingBlock: 'clamp(20px, 2.4vw, 32px)' };

// .card — the inner pages' surface, bordered rather than floating
export const card = {
  position: 'relative',
  background: '#fff',
  border: '1px solid var(--tl-line)',
  borderRadius: '24px',
  transition: `transform .5s ${EASE}, box-shadow .5s ${EASE}, border-color .3s`,
};

// .card.hov — the clickable variant. The round arrow fills and spins to
// point up-right when the whole card is hovered.
export const cardHov = {
  ...card,
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'var(--tl-g9)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: 'var(--tl-sh-3)',
    borderColor: 'transparent',
    color: 'var(--tl-g9)',
  },
  '&:hover .tl-go': { background: 'var(--tl-g9)', color: '#fff', transform: 'rotate(-45deg)' },
};

// .go
export const go = {
  display: 'inline-grid',
  placeItems: 'center',
  width: 40,
  height: 40,
  flex: 'none',
  borderRadius: '50%',
  background: 'var(--tl-mint)',
  color: 'var(--tl-g9)',
  transition: `all .45s ${EASE}`,
};

// .price
export const price = {
  fontFamily: DISPLAY,
  fontSize: 24,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '-.02em',
  fontVariantNumeric: 'tabular-nums',
};

// .chip
export const chip = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: 30,
  padding: '0 12px',
  borderRadius: 999,
  background: 'var(--tl-mint)',
  color: 'var(--tl-g6)',
  fontFamily: UI,
  fontSize: 12.5,
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

export const chipTeal = { ...chip, background: 'var(--tl-teal-50)', color: '#0E6455' };

// .input
export const input = {
  width: '100%',
  height: 52,
  padding: '0 16px',
  border: '1.5px solid var(--tl-line-2)',
  borderRadius: '14px',
  background: '#fff',
  color: 'var(--tl-g9)',
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 500,
  lineHeight: 1.2,
  transition: 'border-color .2s, box-shadow .25s',
  '&::placeholder': { color: '#6A807B', opacity: 1 },
  '&:hover': { borderColor: '#9DBAB2' },
  '&:focus': {
    outline: 'none',
    borderColor: 'var(--tl-teal)',
    boxShadow: '0 0 0 4px rgba(21,122,105,.14)',
  },
};

// .empty
export const empty = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  padding: '64px 24px',
  textAlign: 'center',
  border: '1.5px dashed var(--tl-line-2)',
  borderRadius: '24px',
  background: '#fff',
};

// .ico — the plate inside an empty state
export const ico = {
  display: 'inline-grid',
  placeItems: 'center',
  width: 64,
  height: 64,
  marginBottom: '6px',
  borderRadius: '20px',
  background: 'var(--tl-teal-50)',
  color: '#0E6455',
  flex: 'none',
};

// .sk — skeleton block
export const sk = {
  display: 'block',
  borderRadius: '10px',
  background: 'linear-gradient(90deg, #E8F1EE 0%, #F5FAF8 45%, #E8F1EE 90%)',
  backgroundSize: '300% 100%',
  animation: 'tl-sk 1.5s linear infinite',
};

// .spin
export const spin = {
  width: 18,
  height: 18,
  flex: 'none',
  borderRadius: '50%',
  border: '2px solid currentColor',
  borderRightColor: 'transparent',
  animation: 'tl-spin .8s linear infinite',
};

// ── detail-page shell ───────────────────────────────────────────────────
// .sidebar-l — main column plus a sticky aside. The huge flex-grow on the
// main column means the aside only takes its basis, and the pair wraps to
// one column once the aside can no longer fit.
export const sidebarL = { display: 'flex', flexWrap: 'wrap', gap: '32px', alignItems: 'flex-start' };
export const sidebarMain = { flex: '999 1 560px', minWidth: 0 };
export const sidebarSide = { flex: '1 1 320px', minWidth: 0 };

// A sticky card inside `.side` needs the aside to span the whole row — with
// `sidebarL`'s flex-start the aside sizes to its content, leaving the card no
// room to travel, so it never actually sticks. Pair it with `alignSelf:
// 'stretch'` on the aside at whatever breakpoint the sticky turns on.

// .wrap-n — the narrow wrap, for empty states
export const wrapN = { width: '100%', maxWidth: 880, marginInline: 'auto', paddingInline: 'clamp(16px, 4vw, 40px)' };

// .h-m
export const hM = {
  color: 'var(--tl-g9)',
  fontFamily: DISPLAY,
  fontSize: 'clamp(24px, 2.6vw, 34px)',
  fontWeight: 700,
  letterSpacing: '-.02em',
  lineHeight: 1.08,
  textWrap: 'balance',
  m: 0,
};

// .dsec — one block of the detail page, divided by a rule
export const dsec = {
  scrollMarginTop: 96,
  paddingBlock: '32px',
  borderTop: '1px solid var(--tl-line)',
  '&:first-of-type': { borderTop: 0, paddingTop: 0 },
};

// .kv — label/value row
export const kv = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 240px) 1fr' },
  gap: '6px 24px',
  padding: '15px 0',
  borderBottom: '1px solid var(--tl-line)',
  fontFamily: UI,
  fontSize: 15,
  '& > dt': { color: 'var(--tl-muted)', margin: 0 },
  '& > dd': { margin: 0, fontWeight: 600, color: 'var(--tl-g9)' },
};

// .tile — the small fact squares
export const tile = { background: 'var(--tl-mint)', borderRadius: '24px' };

// .day — schedule pill
export const day = {
  display: 'grid',
  placeItems: 'center',
  width: 44,
  height: 44,
  borderRadius: '12px',
  background: 'var(--tl-mint)',
  color: '#7D918C',
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
};
export const dayOn = { ...day, background: 'var(--tl-teal-50)', color: '#0E6455' };

// .secnav — the in-page jump rail
export const secnav = {
  display: 'flex',
  gap: '6px',
  overflowX: 'auto',
  padding: '6px',
  borderRadius: 999,
  background: '#fff',
  border: '1px solid var(--tl-line)',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '& a': {
    display: 'inline-flex',
    alignItems: 'center',
    // Without this the links shrink to fit instead of scrolling, and the
    // labels pile on top of one another on a narrow screen.
    flex: 'none',
    minHeight: 40,
    padding: '0 14px',
    borderRadius: 999,
    color: 'var(--tl-g6)',
    fontFamily: UI,
    fontSize: 13.5,
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: 'all .25s',
    '&:hover': { background: 'var(--tl-mint)', color: 'var(--tl-g9)' },
  },
};

// .buybox
export const buybox = {
  padding: '28px',
  borderRadius: 'var(--tl-r-xl)',
  background: '#fff',
  border: '1px solid var(--tl-line)',
  boxShadow: 'var(--tl-sh-2)',
};

// .st — status pill with a leading dot
export const st = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  height: 26,
  padding: '0 10px',
  borderRadius: 999,
  fontFamily: UI,
  fontSize: 11.5,
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '.02em',
  whiteSpace: 'nowrap',
  background: 'var(--tl-mint)',
  color: 'var(--tl-g6)',
  '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', background: 'currentColor' },
};
export const stOk = { ...st, background: '#E6F5EC', color: '#147A3C' };

export const chipLine = { ...chip, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--tl-line-2)' };

export const mono = { fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace", fontSize: '.9em', letterSpacing: '-.01em' };

export const mrp = { color: 'var(--tl-muted)', textDecoration: 'line-through', fontSize: 14, fontVariantNumeric: 'tabular-nums' };

// .prose
export const prose = {
  fontFamily: UI,
  fontSize: 17,
  lineHeight: 1.75,
  color: 'var(--tl-g6)',
  '& p + p': { marginTop: '1em' },
  '& ul': { paddingLeft: '1.2em' },
  '& li': { margin: '.35em 0' },
};

// .alert
export const alertTeal = {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  padding: '14px 16px',
  borderRadius: '14px',
  fontFamily: UI,
  fontSize: 15.5,
  lineHeight: 1.5,
  background: 'var(--tl-teal-50)',
  color: '#0E6455',
};

// .acc — native <details> accordion
export const acc = {
  borderTop: '1px solid var(--tl-line)',
  '& details': { borderBottom: '1px solid var(--tl-line)' },
  '& summary': {
    listStyle: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
    minHeight: 68,
    padding: '16px 0',
    cursor: 'pointer',
    fontFamily: DISPLAY,
    fontSize: 17.5,
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: '-.01em',
    color: 'var(--tl-g9)',
  },
  '& summary::-webkit-details-marker': { display: 'none' },
  '& .tl-pm': {
    position: 'relative',
    width: 36,
    height: 36,
    flex: 'none',
    borderRadius: '50%',
    background: 'var(--tl-mint)',
    transition: `transform .45s ${EASE}, background-color .3s`,
  },
  '& .tl-pm::before, & .tl-pm::after': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: 12,
    height: 2,
    margin: '-1px 0 0 -6px',
    borderRadius: '2px',
    background: 'var(--tl-g9)',
    transition: `transform .45s ${EASE}`,
  },
  '& .tl-pm::after': { transform: 'rotate(90deg)' },
  '& details[open] .tl-pm': { background: 'var(--tl-g9)', transform: 'rotate(180deg)' },
  '& details[open] .tl-pm::before, & details[open] .tl-pm::after': { background: '#fff' },
  '& details[open] .tl-pm::after': { transform: 'rotate(0)' },
  '& .tl-body': { padding: '0 56px 24px 0', fontFamily: UI, color: 'var(--tl-muted)', lineHeight: 1.7 },
};

// .mbar — the fixed quick-book bar, below 900px only
export const mbar = {
  display: { xs: 'flex', md: 'none' },
  position: 'fixed',
  left: 12,
  // Clears the WhatsApp FAB, which is fixed bottom-right at every width and
  // was sitting on top of the Book button. The bar itself is hidden from md
  // up, so this gap never shows on desktop — it does not need a breakpoint,
  // and giving it one left the collision in place from 600px to 900px.
  right: 84,
  bottom: 12,
  zIndex: 50,
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '10px 10px 10px 18px',
  borderRadius: '20px',
  background: 'rgba(255,255,255,.94)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid var(--tl-line)',
  boxShadow: 'var(--tl-sh-3)',
  animation: `tl-rise .5s ${EASE} both`,
};

// .cta — the dark closing band
export const ctaDark = {
  position: 'relative',
  overflow: 'hidden',
  padding: 'clamp(28px, 5vw, 56px)',
  borderRadius: 'var(--tl-r-xl)',
  background: 'var(--tl-g9)',
  color: '#fff',
  backgroundImage:
    'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),'
    + 'linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
  backgroundSize: '56px 56px',
};

// ── packages page ───────────────────────────────────────────────────────
// .hl — the yellow marker behind a word in a headline. A gradient rather
// than a background so the band sits on the baseline, not the full line box.
export const hl = {
  background: 'linear-gradient(transparent 60%, rgba(252,235,0,.85) 60%)',
  paddingInline: '.06em',
};

// .split — two columns that collapse to one as soon as either would go
// under 440px, so the quiz card never gets squeezed into a sliver.
export const split = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(440px, 100%), 1fr))',
  gap: 'clamp(32px, 5vw, 72px)',
  alignItems: 'center',
};

// .wrapr — a row of chips/buttons that wraps
export const wrapr = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' };

// .quiz — the white card holding all three states. The min-height stops the
// card jumping as the user moves between a 4-option and a 6-option step, but
// only once it sits beside the headline: stacked under it on a narrow screen
// that floor is just a slab of empty white, so it hugs its content instead.
// 1000px is where `split` stops wrapping (two 440px columns plus the gap).
export const quiz = {
  display: 'flex',
  flexDirection: 'column',
  padding: 'clamp(22px, 3vw, 32px)',
  borderRadius: 'var(--tl-r-xl)',
  background: '#fff',
  boxShadow: '0 40px 80px -40px rgba(11,43,39,.35)',
  '@media (min-width: 1000px)': { minHeight: 460 },
};

// .qopt — one answer. Slides right and darkens its border on hover.
export const qopt = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
  minHeight: 60,
  padding: '0 20px',
  border: '1.5px solid var(--tl-line-2)',
  borderRadius: 'var(--tl-r)',
  background: '#fff',
  color: 'var(--tl-g9)',
  fontFamily: UI,
  fontSize: 16,
  fontWeight: 600,
  textAlign: 'left',
  cursor: 'pointer',
  transition: `all .3s ${EASE}`,
  '& svg': { opacity: 0, transform: 'translateX(-8px)', transition: `all .35s ${EASE}` },
  '&:hover': { borderColor: 'var(--tl-g9)', transform: 'translateX(4px)', boxShadow: 'var(--tl-sh-1)' },
  '&:hover svg': { opacity: 1, transform: 'none' },
};

// .progress — the thin bar over the question
export const progress = { height: 6, borderRadius: 999, background: 'var(--tl-line)', overflow: 'hidden' };
export const progressFill = {
  display: 'block',
  height: '100%',
  borderRadius: 'inherit',
  background: 'var(--tl-teal)',
  transformOrigin: 'left',
  transition: `width .45s ${EASE}`,
};

// .rec — the dark result panel, with a slow dashed ring drifting behind it
export const rec = {
  position: 'relative',
  overflow: 'hidden',
  padding: '28px',
  borderRadius: '24px',
  background: 'var(--tl-g9)',
  color: '#fff',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: -60,
    bottom: -60,
    width: 200,
    height: 200,
    borderRadius: '50%',
    border: '1px dashed rgba(95,224,198,.45)',
    animation: 'tl-spin 50s linear infinite',
    pointerEvents: 'none',
  },
};

// .flag.flag-teal — the corner badge on the featured card. Yellow in v5.
export const flagYellow = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 24,
  padding: '0 9px',
  borderRadius: '7px',
  background: 'var(--tl-yellow)',
  color: 'var(--tl-g9)',
  fontFamily: UI,
  fontSize: 10.5,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

// .textbtn — a bare teal action, 44px tall so it stays tappable
export const textbtn = {
  minHeight: 44,
  padding: '0 4px',
  border: 0,
  background: 'none',
  color: 'var(--tl-teal-d)',
  fontFamily: UI,
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  cursor: 'pointer',
  '&:hover': { background: 'none', color: 'var(--tl-g9)', textDecoration: 'underline' },
};

export const num = { fontVariantNumeric: 'tabular-nums' };

// ── package detail ──────────────────────────────────────────────────────
// .h-xs — the smallest display heading
export const hXs = { color: 'var(--tl-g9)', fontFamily: DISPLAY, fontSize: 17, fontWeight: 700, letterSpacing: '-.005em', m: 0 };

// .ico.ico-sm — the summary band's small rounded icon
export const icoSm = { ...ico, width: 36, height: 36, marginBottom: 0, borderRadius: '10px' };

// .sumcell — one cell of the band under the hero. The rules run vertically
// between cells on desktop and horizontally once they stack.
export const sumcell = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '24px 20px',
  borderRight: '1px solid var(--tl-line)',
  '&:last-of-type': { borderRight: 0 },
  '@media (max-width: 900px)': {
    borderRight: 0,
    borderBottom: '1px solid var(--tl-line)',
    '&:last-of-type': { borderBottom: 0 },
  },
};

// .prep — a numbered preparation step. The counter lives on the list so the
// numbers stay right when a step is conditionally dropped.
export const prepList = { counterReset: 'tl-prep', display: 'flex', flexDirection: 'column', gap: '10px' };
export const prep = {
  counterIncrement: 'tl-prep',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '16px 18px',
  borderRadius: 'var(--tl-r)',
  background: 'var(--tl-mint)',
  fontFamily: UI,
  fontSize: 15.5,
  lineHeight: 1.55,
  color: 'var(--tl-g9)',
  '&::before': {
    content: 'counter(tl-prep)',
    display: 'grid',
    placeItems: 'center',
    width: 28,
    height: 28,
    flex: 'none',
    borderRadius: '50%',
    background: 'var(--tl-g9)',
    color: '#fff',
    fontFamily: UI,
    fontSize: 12.5,
    fontWeight: 700,
  },
};

// .hw-n — the numbered disc in "What happens next"
export const hwN = {
  display: 'grid',
  placeItems: 'center',
  width: 28,
  height: 28,
  flex: 'none',
  borderRadius: '50%',
  background: 'var(--tl-g9)',
  border: '1.5px solid rgba(95,224,198,.5)',
  color: 'var(--tl-teal-l)',
  fontFamily: DISPLAY,
  fontSize: 12.5,
  fontWeight: 800,
};

// .ribbon — the diagonal corner flash on the buy tile when an offer is live
export const ribbon = {
  position: 'absolute',
  top: 18,
  right: -40,
  width: 160,
  padding: '6px 0',
  textAlign: 'center',
  transform: 'rotate(45deg)',
  background: 'var(--tl-gold)',
  color: '#fff',
  fontFamily: UI,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '.14em',
  pointerEvents: 'none',
};

// ── offers page ─────────────────────────────────────────────────────────
// The offers hero swaps the mint ground for a warm cream, which is the one
// place on the site that reads as "sale" without shouting.
export const pheroWarm = { ...phero, background: '#FFF8EE' };

// .eyebrow on the offers hero — same pill, amber lettering
export const eyebrowAmber = { ...eyebrow, color: '#9A5200' };

// .chip.chip-offer
export const chipOffer = { ...chip, background: '#FFF7B3', color: '#6B5A00' };

// .offer-pct — the square discount tile. Springs forward on card hover,
// which is the only motion on an otherwise still card.
export const offerPct = {
  display: 'grid',
  placeItems: 'center',
  alignContent: 'center',
  width: 64,
  height: 64,
  flex: 'none',
  borderRadius: '18px',
  background: '#FFF7B3',
  color: 'var(--tl-g9)',
  transition: `transform .5s ${SPRING}`,
};
export const offerPctNum = { fontFamily: DISPLAY, fontSize: 20, fontWeight: 800, lineHeight: 1, letterSpacing: '-.03em' };
export const offerPctLabel = { fontFamily: UI, fontSize: 10, fontWeight: 800, lineHeight: 1.4, letterSpacing: '.16em' };

// .xs.muted — the small kicker over a card title
export const kicker = {
  fontFamily: UI,
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'var(--tl-muted)',
};

// ── cart ────────────────────────────────────────────────────────────────
// .iconbtn — a bare round control (remove, close). 44px so it stays a
// comfortable tap target even though the glyph inside is small.
export const iconbtn = {
  display: 'inline-grid',
  placeItems: 'center',
  width: 44,
  height: 44,
  flex: 'none',
  border: 0,
  borderRadius: '50%',
  background: 'transparent',
  color: 'var(--tl-muted)',
  cursor: 'pointer',
  transition: `background-color .25s, color .25s`,
  '&:hover': { background: 'var(--tl-mint)', color: 'var(--tl-g9)' },
};

// A destructive text action — "Clear cart". Red, but only on this one verb,
// so it reads as a warning rather than an error state.
export const textbtnDanger = { ...textbtn, color: '#B42318', '&:hover': { background: 'none', color: '#8F1D13', textDecoration: 'underline' } };

// One label/value line in a summary panel.
export const sumRow = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '15px 0',
  borderBottom: '1px solid var(--tl-line)',
  fontFamily: UI,
  fontSize: 15,
};

// ── booking ─────────────────────────────────────────────────────────────
// .btn-lg / .btn-ghost
export const btnLg = { ...btn, minHeight: 58, padding: '0 30px', fontSize: 16 };
export const btnGhost = {
  ...btn,
  background: 'transparent',
  color: 'var(--tl-g9)',
  minHeight: 48,
  boxShadow: 'inset 0 0 0 1.5px var(--tl-line-2)',
  '&::after': { display: 'none' },
  '&:hover': { background: 'var(--tl-mint)', color: 'var(--tl-g9)', boxShadow: 'inset 0 0 0 1.5px var(--tl-g9)' },
  '&:disabled': { opacity: 0.4, pointerEvents: 'none' },
};

// The step rail. A pill that scrolls sideways rather than shrinking, so
// five labels stay readable on a phone instead of collapsing into each other.
export const stepperBar = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  overflowX: 'auto',
  padding: '14px 18px',
  borderRadius: 999,
  background: '#fff',
  border: '1px solid var(--tl-line)',
  boxShadow: 'var(--tl-sh-1)',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};
export const stepNum = {
  display: 'grid',
  placeItems: 'center',
  width: 34,
  height: 34,
  flex: 'none',
  borderRadius: '50%',
  border: '1px solid var(--tl-line-2)',
  background: '#fff',
  color: 'var(--tl-muted)',
  fontFamily: UI,
  fontSize: 14,
  fontWeight: 700,
  transition: `all .3s ${EASE}`,
};
export const stepNumOn = {
  ...stepNum,
  background: 'var(--tl-g9)',
  borderColor: 'var(--tl-g9)',
  color: '#fff',
  boxShadow: '0 0 0 5px rgba(11,43,39,.08)',
};

// .dchip — a day in the date strip
export const dchip = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '2px',
  minWidth: 110,
  minHeight: 68,
  flex: 'none',
  padding: '0 18px',
  borderRadius: '16px',
  border: '1px solid var(--tl-line-2)',
  background: '#fff',
  color: 'var(--tl-g9)',
  fontFamily: UI,
  fontSize: 15,
  textAlign: 'left',
  cursor: 'pointer',
  transition: `all .25s ${EASE}`,
  '&:hover': { borderColor: 'var(--tl-g9)' },
};
export const dchipOn = { ...dchip, background: 'var(--tl-g9)', borderColor: 'var(--tl-g9)', color: '#fff' };

// .pick — a time slot
export const pick = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 44,
  padding: '0 14px',
  borderRadius: '14px',
  border: '1px solid var(--tl-line-2)',
  background: '#fff',
  color: 'var(--tl-g6)',
  fontFamily: UI,
  fontSize: 14,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: `all .25s ${EASE}`,
  '&:hover': { borderColor: 'var(--tl-g9)' },
};
export const pickOn = { ...pick, background: 'var(--tl-g9)', borderColor: 'var(--tl-g9)', color: '#fff' };

// .label / .req — a form field label and its required mark
export const fieldLabel = {
  display: 'block',
  marginBottom: '7px',
  fontFamily: UI,
  fontSize: 13.5,
  fontWeight: 600,
  color: 'var(--tl-g6)',
};
export const req = { color: '#B42318' };

// The step panel the form sits in
export const panelCard = {
  background: '#fff',
  border: '1px solid var(--tl-line)',
  borderRadius: '28px',
  padding: 'clamp(22px, 2.6vw, 36px)',
};

// ── account pages ───────────────────────────────────────────────────────
// More status tones, on the same pill as `st`.
export const stWarn = { ...st, background: '#FFF3E0', color: '#9A5200' };
export const stBad = { ...st, background: '#FEE4E2', color: '#B42318' };
export const stMuted = { ...st, background: 'var(--tl-mint)', color: 'var(--tl-muted)' };

// .kpi — a headline number with its icon
export const kpi = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '24px',
  borderRadius: '24px',
  background: '#fff',
  border: '1px solid var(--tl-line)',
};

// .tblbox / .tbl — a bordered table that scrolls sideways rather than
// crushing its columns, which is what makes these readable on a phone.
export const tblbox = {
  border: '1px solid var(--tl-line)',
  borderRadius: '18px',
  overflow: 'auto',
  background: '#fff',
};
export const tbl = {
  width: '100%',
  minWidth: 620,
  borderCollapse: 'collapse',
  fontFamily: UI,
  '& th': {
    padding: '14px 18px',
    background: 'var(--tl-mint)',
    color: 'var(--tl-muted-2)',
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  '& td': {
    padding: '16px 18px',
    borderBottom: '1px solid var(--tl-line)',
    color: 'var(--tl-g9)',
    fontSize: 14.5,
    verticalAlign: 'middle',
  },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
};

// ── reports gate / role picker ──────────────────────────────────────────
// .btn-teal
// .gate — the centred credential / OTP panel
export const gate = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: 'min(520px, 100%)',
  marginInline: 'auto',
  padding: 'clamp(24px, 4vw, 40px)',
  borderRadius: 'var(--tl-r-xl)',
  background: '#fff',
  border: '1px solid var(--tl-line)',
  boxShadow: 'var(--tl-sh-3)',
};

// .roles — the four portal cards
export const roles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
  gap: '16px',
};
export const role = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '24px',
  borderRadius: '24px',
  background: '#fff',
  border: '1px solid var(--tl-line)',
  textAlign: 'left',
  transition: `transform .45s ${EASE}, box-shadow .45s ${EASE}`,
};
// The one portal this account can actually open — inverted, so the choice
// reads at a glance instead of making people compare four similar cards.
export const roleOpen = {
  ...role,
  background: 'var(--tl-g9)',
  borderColor: 'var(--tl-g9)',
  color: '#fff',
  cursor: 'pointer',
  boxShadow: 'var(--tl-sh-2)',
  '&:hover': { transform: 'translateY(-6px)', boxShadow: 'var(--tl-sh-3)' },
};

// ── reports flow, exact reference values ────────────────────────────────
// .role — measured off the reference: #F8FBFA, 36px radius, 26px padding.
export const roleCard = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  minHeight: 300,
  padding: '26px',
  borderRadius: '36px',
  background: '#F8FBFA',
  border: '1px solid var(--tl-line)',
  textAlign: 'left',
  transition: `transform .45s ${EASE}, box-shadow .45s ${EASE}`,
};
export const roleCardOpen = {
  ...roleCard,
  background: 'var(--tl-g9)',
  borderColor: 'var(--tl-g9)',
  color: '#fff',
  cursor: 'pointer',
  boxShadow: 'var(--tl-sh-2)',
  '&:hover': { transform: 'translateY(-6px)', boxShadow: 'var(--tl-sh-3)' },
};
// The card title: 750 weight is a variable-font axis Bricolage supports.
export const roleTitle = {
  fontFamily: DISPLAY,
  fontSize: 21,
  fontWeight: 750,
  lineHeight: 1.2,
  letterSpacing: '-.01em',
  marginTop: '18px',
};

// .flag, grey — the "Locked" corner tag
export const flagGrey = { ...flagYellow, background: '#D5E5E0', color: '#4A605B', gap: '5px' };

// .okmark — the success tick, with the stroke drawing itself in
export const okmark = {
  display: 'grid',
  placeItems: 'center',
  width: 84,
  height: 84,
  borderRadius: '50%',
  background: '#E6F5EC',
  color: '#147A3C',
  boxShadow: '0 0 0 10px rgba(20,122,60,.06)',
  animation: `tl-chipin .6s ${SPRING} both`,
  '& path': { strokeDasharray: 30, strokeDashoffset: 30, animation: `tl-draw .6s ${EASE} .3s forwards` },
};

// .alert-bad
export const alertBad = {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  padding: '14px 16px',
  borderRadius: 'var(--tl-r)',
  background: '#FEE4E2',
  color: '#B42318',
  fontFamily: UI,
  fontSize: 14.5,
  lineHeight: 1.5,
};

// ── track ───────────────────────────────────────────────────────────────
// The five stage markers. Done is a filled tick, the current one a ring with
// a dot, the rest an empty ring — so progress reads without colour alone.
const stageBase = {
  display: 'grid',
  placeItems: 'center',
  width: 28,
  height: 28,
  flex: 'none',
  borderRadius: '50%',
  border: '2px solid var(--tl-line-2)',
  background: '#fff',
  color: 'transparent',
  transition: `all .4s ${EASE}`,
};
export const stageDot = stageBase;
export const stageDotDone = { ...stageBase, background: 'var(--tl-teal-d)', borderColor: 'var(--tl-teal-d)', color: '#fff' };
export const stageDotNow = {
  ...stageBase,
  borderColor: 'var(--tl-teal-d)',
  boxShadow: '0 0 0 5px rgba(20,122,60,.08)',
  '&::after': { content: '""', width: 10, height: 10, borderRadius: '50%', background: 'var(--tl-teal-d)' },
};

// A dark panel used for a single aside note (the reports-portal card).
export const cardDark = {
  padding: '24px',
  borderRadius: '24px',
  background: 'var(--tl-g9)',
  color: '#fff',
};

// ── my tickets ──────────────────────────────────────────────────────────
// .tkrow — one ticket in the list
export const tkrow = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto auto' },
  alignItems: 'center',
  gap: { xs: '10px 14px', sm: '6px 18px' },
  width: '100%',
  padding: '18px 20px',
  borderRadius: '20px',
  background: '#fff',
  border: '1px solid var(--tl-line)',
  textAlign: 'left',
  cursor: 'pointer',
  transition: `transform .35s ${EASE}, box-shadow .35s ${EASE}, border-color .25s`,
  '&:hover': { borderColor: 'var(--tl-line-2)', boxShadow: 'var(--tl-sh-1)', transform: 'translateY(-2px)' },
  '&:hover .tl-tkarr': { transform: 'translateX(3px)', color: 'var(--tl-g9)' },
};
export const tkArr = { display: 'grid', placeItems: 'center', flex: 'none', color: 'var(--tl-muted)', transition: `all .3s ${EASE}` };

// .flag variants, measured off the reference
// These labels are long ("Corrective action recorded"), and `flagYellow` is
// nowrap — on a phone it ran straight off the edge of the card. Allow a
// second line; at desktop width they still sit on one.
const flagWrap = { whiteSpace: 'normal', height: 'auto', minHeight: 24, paddingBlock: '4px', lineHeight: 1.3 };
export const flagInfo = { ...flagYellow, ...flagWrap, background: '#9A5200', color: '#fff' };
export const flagInk = { ...flagYellow, ...flagWrap, background: 'var(--tl-g9)', color: '#fff' };
export const flagNew = { ...flagYellow, ...flagWrap };

// .wrap-n on this page is wider than the 880 used for empty states.
export const wrapTickets = { width: '100%', maxWidth: 1000, marginInline: 'auto', paddingInline: 'clamp(16px, 4vw, 40px)' };

// ── cancel booking ──────────────────────────────────────────────────────
export const stInfo = { ...st, background: '#E8EEFF', color: '#1E40AF' };

// A destructive primary button — used once, on the confirm.
export const btnDanger = {
  ...btn,
  background: '#B42318',
  color: '#fff',
  '&:hover': { background: '#8F1D13', color: '#fff', transform: 'translateY(-2px)', boxShadow: 'var(--tl-sh-2)' },
  '&:disabled': { opacity: 0.55, pointerEvents: 'none' },
};

// .sw — the refund-to-bank toggle. A real checkbox underneath, so it is
// keyboard-reachable and announces its state; this only paints it.
export const sw = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  width: 52,
  height: 30,
  flex: 'none',
  borderRadius: 999,
  background: 'var(--tl-line-2)',
  cursor: 'pointer',
  transition: `background-color .3s ${EASE}`,
  '& input': { position: 'absolute', inset: 0, opacity: 0, margin: 0, cursor: 'pointer' },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 3,
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: '#fff',
    boxShadow: 'var(--tl-sh-1)',
    transition: `transform .3s ${EASE}`,
  },
  '&:has(input:checked)': { background: 'var(--tl-teal-d)' },
  '&:has(input:checked)::after': { transform: 'translateX(22px)' },
  '&:has(input:focus-visible)': { outline: '2px solid var(--tl-g9)', outlineOffset: 2 },
};

// .h-l — between h-xl and h-m. The auth screens' headline (50px at 1440).
export const hL = {
  color: 'var(--tl-g9)',
  fontFamily: DISPLAY,
  fontSize: 'clamp(30px, 4vw, 50px)',
  fontWeight: 700,
  letterSpacing: '-.04em',
  lineHeight: 1.08,
  textWrap: 'balance',
  m: 0,
};

// ── membership plans ────────────────────────────────────────────────────
export const plans = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
  gap: '20px',
  alignItems: 'stretch',
};
export const plan = {
  display: 'flex',
  flexDirection: 'column',
  padding: '32px',
  borderRadius: 'var(--tl-r-xl)',
  background: '#fff',
  border: '1px solid var(--tl-line)',
  boxShadow: 'var(--tl-sh-2)',
  transition: `transform .5s ${EASE}, box-shadow .5s ${EASE}`,
  '&:hover': { transform: 'translateY(-6px)', boxShadow: 'var(--tl-sh-3)' },
};
// The featured plan inverts and sits proud of the row.
export const planPop = {
  ...plan,
  background: 'var(--tl-g9)',
  borderColor: 'var(--tl-g9)',
  color: '#fff',
  boxShadow: '0 50px 90px -40px rgba(11,43,39,.6)',
  transform: 'translateY(-16px)',
  '&:hover': { transform: 'translateY(-22px)', boxShadow: '0 56px 100px -40px rgba(11,43,39,.65)' },
};
export const planChk = {
  display: 'grid',
  placeItems: 'center',
  width: 22,
  height: 22,
  flex: 'none',
  marginTop: '1px',
  borderRadius: '50%',
  background: 'var(--tl-teal-50)',
  color: 'var(--tl-teal-d)',
};

// WhatsApp green — used on the FAQ and Support CTA bands.
export const btnWa = {
  ...btn,
  background: 'var(--tl-wa)',
  color: '#fff',
  '&:hover': { background: '#128C45', color: '#fff', transform: 'translateY(-2px)', boxShadow: 'var(--tl-sh-2)' },
};

// ── tabs ────────────────────────────────────────────────────────────────
// A pill group that scrolls sideways rather than wrapping, so a long city
// list stays on one line on a phone.
export const tabs = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  maxWidth: '100%',
  overflowX: 'auto',
  padding: '5px',
  borderRadius: 999,
  background: 'var(--tl-mint)',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};
export const tab = {
  flex: 'none',
  minHeight: 40,
  padding: '0 18px',
  border: 0,
  borderRadius: 999,
  background: 'transparent',
  color: 'var(--tl-muted-2)',
  fontFamily: UI,
  fontSize: 14.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: `all .25s ${EASE}`,
  '&:hover': { color: 'var(--tl-g9)' },
};
export const tabOn = { ...tab, background: 'var(--tl-g9)', color: '#fff', '&:hover': { color: '#fff' } };

// A decorative street-grid thumbnail with a pin, for a centre card.
export const mapThumb = {
  position: 'relative',
  height: 132,
  display: 'grid',
  placeItems: 'center',
  background: 'var(--tl-mint)',
  backgroundImage:
    'linear-gradient(#fff 2px, transparent 2px), linear-gradient(90deg, #fff 2px, transparent 2px)',
  backgroundSize: '64px 46px',
  color: 'var(--tl-teal-d)',
};

// ── about ───────────────────────────────────────────────────────────────
// Everything below is transcribed from the reference's About artboard.

// .h-hero — only the About hero uses this; it runs larger than .h-xl and
// at a line-height under 1, which is what makes the two lines interlock.
export const hHero = {
  color: '#fff',
  fontFamily: DISPLAY,
  fontSize: 'clamp(42px, 6.4vw, 84px)',
  fontWeight: 800,
  letterSpacing: '-.04em',
  lineHeight: 0.95,
  textWrap: 'balance',
  m: 0,
};

// .hero-orb — the teal haze bleeding off the hero's top-right corner.
export const heroOrb = {
  position: 'absolute',
  right: '-12%',
  top: '-30%',
  width: '62vw',
  maxWidth: 900,
  aspectRatio: '1',
  borderRadius: '50%',
  background: 'radial-gradient(circle at 50% 50%, rgba(95,224,198,.22), rgba(95,224,198,0) 62%)',
  pointerEvents: 'none',
};

// .gridbg — the faint 56px graticule over a dark band.
export const gridbg = {
  backgroundImage:
    'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),'
    + 'linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
  backgroundSize: '56px 56px',
};

// .about-stats — the four figures under the hero headline, over a rule.
export const aboutStats = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
  gap: '24px',
  paddingTop: '28px',
  borderTop: '1px solid rgba(255,255,255,.14)',
  '& > div': { display: 'flex', flexDirection: 'column', gap: '6px' },
  '& span': { color: 'var(--tl-soft)', fontFamily: UI, fontSize: 14.5 },
};

// .timeline — the dashed rail with a ringed node per milestone. The rail is
// drawn by ::before on the list and the nodes by ::before on each item, so
// the markup stays a plain <ol>.
export const timeline = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  listStyle: 'none',
  m: 0,
  p: 0,
  paddingLeft: '34px',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 8,
    top: 8,
    bottom: 8,
    width: 2,
    background: 'repeating-linear-gradient(var(--tl-line-2) 0 6px, transparent 6px 12px)',
  },
  '& > li': {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '0 0 40px',
  },
  '& > li::before': {
    content: '""',
    position: 'absolute',
    left: -32,
    top: 6,
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: '#fff',
    border: '3px solid var(--tl-teal-d)',
  },
};

// .tl-yr — the year, set in the mono face.
export const tlYr = {
  fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '.04em',
  color: 'var(--tl-teal-d)',
};

// .mv — the mission / vision pair. The min-height keeps the two the same
// size when one statement is a line shorter than the other.
export const mv = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '18px',
  minHeight: 260,
  padding: 'clamp(28px, 4vw, 44px)',
  borderRadius: 'var(--tl-r-xl)',
  background: 'var(--tl-teal-50)',
};
export const mvDark = { ...mv, background: 'var(--tl-g9)', color: '#fff' };

// .member — a leadership tile. The whole tile is the button; the portrait
// plate pushes in slightly as it lifts.
export const member = {
  display: 'flex',
  flexDirection: 'column',
  // A <button> is shrink-to-fit even as a flex container, so without this
  // each tile sizes to its own longest line and the row comes out ragged.
  // In the reference the button is the grid item itself and stretches; here
  // it sits one wrapper deep.
  width: '100%',
  padding: 0,
  overflow: 'hidden',
  border: '1px solid var(--tl-line)',
  borderRadius: 'var(--tl-r-l)',
  background: '#fff',
  color: 'var(--tl-g9)',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  transition: `transform .5s ${EASE}, box-shadow .5s ${EASE}`,
  '&:hover': { transform: 'translateY(-6px)', boxShadow: 'var(--tl-sh-3)' },
  '&:hover .tl-av': { transform: 'scale(1.05)' },
};

// .member-av — initials on a mint plate, or the admin's photo when set.
export const memberAv = {
  display: 'grid',
  placeItems: 'center',
  aspectRatio: '1.15',
  background: 'linear-gradient(160deg, var(--tl-mint-2), var(--tl-mint))',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  fontFamily: DISPLAY,
  fontSize: 44,
  fontWeight: 800,
  lineHeight: 1,
  color: 'var(--tl-g6)',
  transition: `transform .7s ${EASE}`,
};

// .feat — a differentiator card, which only ever sits on a dark band.
export const feat = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '26px',
  borderRadius: 'var(--tl-r-l)',
  background: 'rgba(255,255,255,.05)',
  border: '1px solid rgba(255,255,255,.08)',
  transition: `background-color .3s, transform .5s ${EASE}`,
  '&:hover': { background: 'rgba(255,255,255,.09)', transform: 'translateY(-4px)' },
};

// .ico.ico-dark — the icon plate's dark-band variant.
export const icoDark = {
  ...ico,
  width: 44,
  height: 44,
  marginBottom: 0,
  borderRadius: '14px',
  background: 'rgba(95,224,198,.12)',
  color: 'var(--tl-teal-l)',
};

// .chan — a contact channel row, used as a link.
export const chan = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '16px 18px',
  borderRadius: '20px',
  border: '1px solid var(--tl-line)',
  background: '#fff',
  color: 'var(--tl-g9)',
  textDecoration: 'none',
  transition: `transform .4s ${EASE}, box-shadow .4s ${EASE}`,
  '&:hover': { color: 'var(--tl-g9)', transform: 'translateY(-3px)', boxShadow: 'var(--tl-sh-2)' },
};

// .cta — the closing band. `ctaDark` already carries the dark panel used on
// other pages; this is the About variant, which also holds a drifting ring.
export const cta = {
  position: 'relative',
  overflow: 'hidden',
  padding: 'clamp(36px, 6vw, 80px)',
  borderRadius: 'var(--tl-r-xl)',
  background: 'var(--tl-g9)',
  color: '#fff',
};
export const ctaRing = {
  position: 'absolute',
  right: -120,
  top: -120,
  width: 460,
  height: 460,
  borderRadius: '50%',
  border: '1px dashed rgba(95,224,198,.4)',
  animation: 'tl-spin 80s linear infinite',
  pointerEvents: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 70,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,.08)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: -7,
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: 'var(--tl-teal-l)',
    boxShadow: '0 0 24px var(--tl-teal-l)',
  },
};

// .trustian-vis — the dark plate with a floating badge inside a slow ring.
export const trustianVis = {
  position: 'relative',
  display: 'grid',
  placeItems: 'center',
  minHeight: 380,
  borderRadius: 'var(--tl-r-xl)',
  background: 'var(--tl-g9)',
  overflow: 'hidden',
};
export const tvRing = {
  position: 'absolute',
  width: 420,
  height: 420,
  borderRadius: '50%',
  border: '1px dashed rgba(95,224,198,.45)',
  animation: 'tl-spin 50s linear infinite',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: -7,
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: 'var(--tl-teal-l)',
    boxShadow: '0 0 24px var(--tl-teal-l)',
  },
};
export const tvBadge = {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '24px 28px',
  borderRadius: '24px',
  background: '#fff',
  color: 'var(--tl-g9)',
  boxShadow: 'var(--tl-sh-3)',
  animation: 'tl-float 7s ease-in-out infinite',
};

// .live — the pulsing "on duty" marker.
export const live = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: UI,
  fontSize: 11,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '.14em',
  color: '#147A3C',
  '& i': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#16A34A',
    animation: 'tl-beat 1.8s infinite',
  },
};

// ── forms shared by the two About dialogs ───────────────────────────────
// .fgrid — a field grid that drops to one column under 260px per cell.
export const fgrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
  gap: '20px',
};

// .sel — wraps a <select> so the chevron can be drawn without a background
// image; the select itself keeps `input`'s box.
export const sel = {
  position: 'relative',
  '& select': { appearance: 'none', paddingRight: '44px', cursor: 'pointer' },
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 20,
    top: '50%',
    width: 8,
    height: 8,
    borderRight: '2px solid var(--tl-muted)',
    borderBottom: '2px solid var(--tl-muted)',
    transform: 'translateY(-70%) rotate(45deg)',
    pointerEvents: 'none',
  },
};

// .opt — a radio dressed as a selectable tile. `:has` lights the whole
// tile from the input inside it, so the label needs no state of its own.
export const opt = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minHeight: 56,
  padding: '10px 18px',
  border: '1.5px solid var(--tl-line-2)',
  borderRadius: '14px',
  background: '#fff',
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--tl-g9)',
  cursor: 'pointer',
  transition: `all .25s ${EASE}`,
  '& input': { accentColor: 'var(--tl-teal-d)', width: 20, height: 20, margin: 0, flex: 'none', cursor: 'pointer' },
  '&:hover': { borderColor: '#9DBAB2' },
  '&:has(input:checked)': {
    borderColor: 'var(--tl-teal-d)',
    background: 'var(--tl-teal-50)',
    boxShadow: '0 0 0 3px rgba(21,122,105,.08)',
  },
};

// .check — the consent line.
export const check = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  fontFamily: UI,
  fontSize: 14.5,
  lineHeight: 1.5,
  color: 'var(--tl-g6)',
  cursor: 'pointer',
  '& input': { accentColor: 'var(--tl-teal-d)', width: 20, height: 20, margin: '1px 0 0', flex: 'none', cursor: 'pointer' },
};

// .flag-teal — in the v5 layer this is the yellow flag, not a teal one.
export const flagTeal = { ...flagYellow, background: 'var(--tl-yellow)', color: 'var(--tl-g9)' };

// .btn-sm
export const btnSm = { ...btn, minHeight: 44, padding: '0 18px', fontSize: 14 };

// .sticky — a column that holds its heading while the other column scrolls.
// It only works where the row is taller than the column, so the parent must
// stretch rather than centre.
export const sticky = { position: 'sticky', top: 96 };

// ── corporate ───────────────────────────────────────────────────────────
// Transcribed from the reference's Corporate artboard.

// .xs — the smallest label size in the system.
export const xs = { fontFamily: UI, fontSize: 12.5, lineHeight: 1.45 };

// .chip-dark — a chip that sits on a dark band.
export const chipDark = { ...chip, background: 'rgba(255,255,255,.1)', color: '#fff' };

// .statsbar — the translucent slab under the hero headline. Cells flex
// so a wide one (the risk bar) can take two shares of the row.
export const statsbar = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '28px 40px',
  padding: '26px 28px',
  borderRadius: 'var(--tl-r-xl)',
  background: 'rgba(255,255,255,.06)',
  border: '1px solid rgba(255,255,255,.1)',
  '& > *': { flex: '1 1 140px' },
};

// .riskbar — healthy / borderline / high-risk as one stacked bar. Each
// segment grows from the left on first paint, staggered.
export const riskbar = {
  display: 'flex',
  gap: '2px',
  height: 30,
  borderRadius: '8px',
  overflow: 'hidden',
  '& > span': {
    display: 'grid',
    placeItems: 'center',
    minWidth: 0,
    color: '#fff',
    fontFamily: UI,
    fontSize: 12,
    fontWeight: 700,
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    animation: `tl-fillx 1.2s ${EASE} both`,
  },
  '& > span:nth-of-type(2)': { animationDelay: '.12s' },
  '& > span:nth-of-type(3)': { animationDelay: '.24s' },
};
export const riskbarSm = { ...riskbar, height: 12, borderRadius: '4px' };

// .lg — the little legend swatch beside "Healthy" / "Borderline".
export const legendDot = { display: 'inline-block', width: 10, height: 10, borderRadius: '3px', flex: 'none' };

// .why — a numbered reason. The counter is drawn by CSS so the list stays
// a plain <ol> and the numbers can never fall out of step with the items.
export const whyList = { counterReset: 'tl-why', listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '12px' };
export const why = {
  counterIncrement: 'tl-why',
  display: 'grid',
  gridTemplateColumns: '44px 1fr',
  gap: '4px 16px',
  padding: '20px 22px',
  borderRadius: 'var(--tl-r-l)',
  background: 'var(--tl-mint)',
  transition: `background-color .3s, transform .4s ${EASE}`,
  '&::before': {
    content: 'counter(tl-why, decimal-leading-zero)',
    gridRow: 'span 2',
    fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
    fontSize: 15,
    fontWeight: 800,
    color: 'var(--tl-teal-d)',
  },
  '&:hover': { background: 'var(--tl-mint-2)', transform: 'translateX(4px)' },
};

// .journey — the dashed rail with a dot that travels along it.
export const journey = {
  position: 'relative',
  height: 2,
  background: 'repeating-linear-gradient(90deg, rgba(255,255,255,.22) 0 6px, transparent 6px 12px)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -5,
    left: 0,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: 'var(--tl-teal)',
    boxShadow: '0 0 0 6px rgba(35,169,145,.18)',
    animation: `tl-travel 7s ${EASE} infinite`,
  },
};

// .hw / .hw-line / .hw-grid / .hw-step — the staged-engagement row. The
// rail is absolutely positioned behind the discs, so it is hidden on a
// phone where the steps stack and it would run vertically through them.
export const hw = { position: 'relative' };
// The rail only makes sense while every step sits on one row. Five steps
// at minmax(170px, 1fr) need ~1010px of viewport; below `lg` the grid
// wraps and the rail would float over the second row pointing at nothing.
export const hwLine = { position: 'absolute', left: '3%', right: '3%', top: 27, display: { xs: 'none', lg: 'block' } };
export const hwGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(170px, 100%), 1fr))',
  gap: '32px 20px',
  position: 'relative',
  listStyle: 'none',
  m: 0,
  p: 0,
};
export const hwStep = { display: 'flex', flexDirection: 'column', gap: '10px' };
// The large disc, as distinct from `hwN`'s 28px one on the detail pages.
export const hwDisc = {
  display: 'grid',
  placeItems: 'center',
  width: 56,
  height: 56,
  marginBottom: '10px',
  borderRadius: '50%',
  background: 'var(--tl-g9)',
  border: '1.5px solid rgba(95,224,198,.5)',
  color: 'var(--tl-teal-l)',
  fontFamily: DISPLAY,
  fontSize: 15,
  fontWeight: 800,
  transition: `all .4s ${EASE}`,
};

// .dash — the HR dashboard mock. It only ever holds its own chrome, so
// the radius clips the tab strip against the card edge.
export const dash = { overflow: 'hidden', borderRadius: 'var(--tl-r-xl)', boxShadow: 'var(--tl-sh-3)', background: '#fff' };

// .rung — the numbered ring in the commitment list on the dark aside.
export const rung = {
  display: 'grid',
  placeItems: 'center',
  width: 28,
  height: 28,
  flex: 'none',
  borderRadius: '50%',
  border: '1.5px solid var(--tl-teal-l)',
  color: 'var(--tl-teal-l)',
  fontFamily: DISPLAY,
  fontSize: 12,
  fontWeight: 800,
};

// .sel on a dark band. The light-panel `sel` draws its chevron in
// `--tl-muted`, which is invisible here, and a bare <select> falls back to
// `appearance: auto` — the OS then paints its own control, which on a dark
// panel reads as a disabled grey box. This turns that off and draws the
// chevron in the teal glow instead.
export const selDark = {
  position: 'relative',
  '& select': {
    appearance: 'none',
    paddingRight: '48px',
    cursor: 'pointer',
  },
  // Chrome and Firefox honour this, so the open list matches the panel;
  // Safari always uses the system list, which is why the closed control
  // has to stand on its own.
  '& option': { background: 'var(--tl-g9)', color: '#fff' },
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 22,
    top: '50%',
    width: 9,
    height: 9,
    borderRight: '2px solid var(--tl-teal-l)',
    borderBottom: '2px solid var(--tl-teal-l)',
    transform: 'translateY(-70%) rotate(45deg)',
    pointerEvents: 'none',
    transition: `transform .25s ${EASE}`,
  },
  '&:focus-within::after': { transform: 'translateY(-20%) rotate(225deg)' },
};
